import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { gaTrackEvent } from "../../analytics/ga4";
import { getI18nStrings } from "../../config/runtime";
import { LOCALE_STORAGE_KEY, SUPPORTED_LOCALES, VI_PRICE_MAP } from "../../core/constants";
import { resolveClientLocale } from "../../core/localePaths";

const LocaleContext = createContext(null);

function normalizeLocaleValue(raw) {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase()
    .slice(0, 2);
  return SUPPORTED_LOCALES.includes(s) ? s : "en";
}

function applyDocumentLocale(loc) {
  if (typeof document !== "undefined") {
    document.documentElement.lang = loc;
  }
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, loc);
  } catch (_) {
    // ignore storage failures
  }
}

export function LocaleProvider({ children, serverLocale = null }) {
  const [locale, setLocaleState] = useState(() => {
    const initial = normalizeLocaleValue(resolveClientLocale(serverLocale));
    applyDocumentLocale(initial);
    return initial;
  });
  const setLocale = useCallback((nextLocale) => {
    const normalizedLocale = SUPPORTED_LOCALES.includes(nextLocale) ? nextLocale : "en";
    setLocaleState(normalizedLocale);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, normalizedLocale);
    } catch (_) {
      // ignore storage failures
    }
    document.documentElement.lang = normalizedLocale;
    gaTrackEvent("locale_change", { locale: normalizedLocale });
  }, []);

  const value = useMemo(() => {
    const t = (key, defaultValue) => {
      const table = getI18nStrings();
      const raw = table[locale]?.[key] ?? table.en?.[key];
      if (raw != null && raw !== "") return raw;
      return defaultValue !== undefined ? defaultValue : key;
    };
    const displayPrice = (usdPrice) => (locale === "vi" ? VI_PRICE_MAP[usdPrice] || usdPrice : usdPrice);

    return { locale, setLocale, t, displayPrice };
  }, [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useI18n must be used within LocaleProvider");
  return ctx;
}
